import { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
  getCountries,
  getWorldTimezones,
  getAvailableCountries,
  getWorldLanguages,
} from 'routing/fetchingfunction/info/info.action'

import { formatCountry, formatLanguages, formatTimezones } from 'helpers'

const useGetInfo = () => {
  const dispatch = useDispatch()
  const { countries, availableCountries, worldLanguages, worldTimezones } = useSelector(
    (state) => state.info
  )

  // Track loading state to prevent multiple API calls
  const isLoading = useSelector((state) => state.info.isLoading)

  // Directly load all required data on component mount to ensure it's available
  useEffect(() => {
    // Skip loading if already in progress
    if (isLoading) {
      return
    }

    const loadRequiredData = async () => {
      console.log('Loading dropdown data for registration...')

      // Create an array of promises for all data we need to load
      const loadPromises = []

      if (countries.length === 0) {
        console.log('Loading countries data...')
        loadPromises.push(dispatch(getCountries()))
      }

      if (availableCountries.length === 0) {
        console.log('Loading available countries data...')
        loadPromises.push(dispatch(getAvailableCountries()))
      }

      if (worldLanguages.length === 0) {
        console.log('Loading languages data...')
        loadPromises.push(dispatch(getWorldLanguages()))
      }

      if (worldTimezones.length === 0) {
        console.log('Loading timezones data...')
        loadPromises.push(dispatch(getWorldTimezones()))
      }

      // Wait for all data to load in parallel
      if (loadPromises.length > 0) {
        try {
          await Promise.all(loadPromises)
          console.log('All dropdown data loaded successfully')
        } catch (error) {
          console.error('Error loading dropdown data:', error)
        }
      }
    }

    loadRequiredData()
    // We're intentionally not including the length properties in dependencies
    // to prevent infinite loops - only run this effect once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isLoading])

  // Format and memoize the formatted data to prevent unnecessary re-renders
  const formattedCountries = useMemo(() => {
    return formatCountry(countries).sort((a, b) => a.label.localeCompare(b.label))
  }, [countries])

  const formattedAvailableCountries = useMemo(() => {
    return formatCountry(availableCountries).sort((a, b) => a.label.localeCompare(b.label))
  }, [availableCountries])

  const formattedWorldLanguages = useMemo(() => {
    return formatLanguages(worldLanguages).sort((a, b) => a.label.localeCompare(b.label))
  }, [worldLanguages])

  const formattedWorldTimezones = useMemo(() => {
    return formatTimezones(worldTimezones).sort((a, b) => a.label.localeCompare(b.label))
  }, [worldTimezones])

  return {
    countries,
    availableCountries,
    worldLanguages,
    worldTimezones,
    formattedCountries,
    formattedAvailableCountries,
    formattedWorldLanguages,
    formattedWorldTimezones,
  }
}

export default useGetInfo
